import { Record, List, Map, fromJS } from "immutable";
import { IAddressData, addressFromJSON, AddressRecord } from "common/records/Address";
import { IImageData, ImageRecord, imageFromJSON } from "common/records/Image";
import { IPhoneNumberData, phoneFromJSON as phoneFromJSON, PhoneNumberRecord } from "common/records/PhoneNumber";
import { fromJSON as overviewFromJSON } from "common/records/EstimateOverview";
import { jobAssignmentFromJSON, JobAssignmentRecord, IJobAssignmentData } from "common/records/JobAssignment";
import { DateStringNumber, Nullable, YesNoProp } from "common/records";
import { IEstimateOverviewData, EstimateOverviewRecord } from "common/records/EstimateOverview";
import { documentFromJSON, IDocumentData, DocumentRecord } from "common/records/Document";
import { IEstimateComparisonData } from "common/records/EstimateComparison";
import { IMeasurementData } from "common/records/Measurement";
import { IOrgData } from "common/records/Org";
import { IJobTypeData } from "common/records/JobType";
import { RootFolderNames } from "common/records/Folder";
import { checkDate } from "common/helpers/Date";
import { IJobStateLogData } from "common/records/JobStateLog";

export const fromJSON = (json: Partial<IJobData>): JobRecord => {
  const record: IJobRecord = { ...(json as any) };

  if (json.address) {
    record.address = addressFromJSON(json.address);
  } else {
    record.address = addressFromJSON({});
  }

  if (json.customer_address) {
    record.customer_address = addressFromJSON(json.customer_address);
  } else {
    record.customer_address = addressFromJSON({});
  }

  if (json.phone_numbers && json.phone_numbers.length > 0) {
    record.phone_numbers = List<PhoneNumberRecord>(json.phone_numbers.map((pn) => phoneFromJSON(pn)));
  } else {
    record.phone_numbers = List<PhoneNumberRecord>([phoneFromJSON({})]);
  }

  if (json.assignments) {
    record.assignments = List<JobAssignmentRecord>(
      json.assignments.map((assignment) => jobAssignmentFromJSON(assignment)),
    );
  } else {
    record.assignments = List<JobAssignmentRecord>();
  }

  if (json.images) {
    record.images = List<ImageRecord>(json.images.map((i) => imageFromJSON(i)));
  } else {
    record.images = List<ImageRecord>();
  }

  if (json.documents) {
    record.documents = List<DocumentRecord>(json.documents.map((i) => documentFromJSON(i)));
  } else {
    record.documents = List<DocumentRecord>();
  }

  if (json.estimates) {
    record.estimates = List<EstimateOverviewRecord>(json.estimates.map((i) => overviewFromJSON(i)));
  } else {
    record.estimates = List<EstimateOverviewRecord>();
  }

  if (json.inspections) {
    record.inspections = List<EstimateOverviewRecord>(json.inspections.map((i) => overviewFromJSON(i)));
  } else {
    record.inspections = List<EstimateOverviewRecord>();
  }

  if (json.measurement) {
    record.measurement_id = json.measurement.id;
  }

  if (json.config) {
    record.config = fromJS(json.config);
  }

  if (json.metadata) {
    record.metadata = fromJS(json.metadata);
  }

  if (json.appointments) {
    record.appointments = List(
      json.appointments.map((i) => Map<"start_time", Date>({ start_time: new Date(i.start_time) })),
    );
  }

  if (json.events) {
    record.events = List<string>(json.events);
  }

  if (json.job_types) {
    record.job_type_ids = List<number>(json.job_types.map((i) => i.id));
  }

  if (json.folders) {
    record.folders = Map<RootFolderNames, number>(json.folders);
  }

  if (json.errors) {
    record.errors = List<string>(json.errors);
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new JobRecord(record);
};

export const fetchCustomerAddress = (job: JobRecord): AddressRecord => {
  if (job.use_job_address === "yes") {
    return job.address;
  }

  return job.customer_address;
};

export const validMetadataIndices = [0, 1, 2, 3, 4, 5];

interface IJobsPageData {
  jobs: List<number>;
  errors: List<string>;
  loading: boolean;
}

export interface IJobStateEventInfo {
  from: string | Array<string>;
  to: string;
  on: string;
}

export interface IJobStateInfo {
  pretty_name: string;
  color: string;
}

export type IJobsPageRecord = Record<IJobsPageData>;

export const JobsPageRecord: Record.Factory<IJobsPageData> = Record<IJobsPageData>({
  jobs: List<number>(),
  errors: List<string>(),
  loading: false,
});

export interface IJobPostData {
  id: number;
  uid: string;
  org_id: number;
  name?: string;
  customer_name?: string;
  email: string;
  state: string;
  raw_state: string;
  lead_source: string;
  config: any;
  use_job_address: YesNoProp;
  address_attributes?: IAddressData;
  customer_address_attributes?: IAddressData;
  phone_numbers_attributes?: IPhoneNumberData[];
  cover_image_uuid?: string;
}

export interface IJobData {
  id: number;
  uid: string;
  uuid: string;
  org_id: number;
  org_uid: string;
  name?: string;
  customer_name?: string;
  email?: string;
  state?: string;
  config: any;
  raw_state?: string;
  lead_source?: string;
  loading?: boolean;
  address?: IAddressData;
  customer_address?: IAddressData;
  phone_numbers?: IPhoneNumberData[];
  cover_image_uuid?: string;
  estimate_count?: number;
  estimates?: IEstimateOverviewData[];
  inspections?: IEstimateOverviewData[];
  measurement: IMeasurementData;
  archived: boolean;
  viewed: boolean;
  document_count: number;
  image_count: number;
  use_job_address: YesNoProp;
  events: string[];
  job_types: IJobTypeData[];
  folder_id?: number | null;
  folders: { [key in RootFolderNames]?: number };

  org?: IOrgData;
  images?: Array<IImageData>;
  documents?: Array<IDocumentData>;
  assignments?: Array<IJobAssignmentData>;
  additional_assignment?: IJobAssignmentData;
  estimate_comparison: IEstimateComparisonData;
  additional_info: string;
  metadata: any;
  appointments: Array<{ start_time: string }>;
  state_logs?: Array<IJobStateLogData>;

  errors: string[];

  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IJobRecord {
  id: number;
  uid: string;
  uuid: string;
  name: Nullable<string>;
  org_id: number;
  org_uid: string;
  customer_name: Nullable<string>;
  state: Nullable<string>;
  stateAction: Nullable<string>;
  config: Map<any, any>;
  raw_state: Nullable<string>;
  lead_source: Nullable<string>;
  email: Nullable<string>;
  address: AddressRecord;
  customer_address: AddressRecord;
  cover_image_uuid: Nullable<string>;
  estimate_count: number;
  measurement_id: number;
  viewed: boolean;
  loading: boolean;
  archived: boolean;
  document_count: number;
  image_count: number;
  use_job_address: YesNoProp;
  events: List<string>;
  job_type_ids: List<number>;
  folder_id: Nullable<number>;
  folders: Map<RootFolderNames, number>;

  phone_numbers: List<PhoneNumberRecord>;
  images: List<ImageRecord>;
  documents: List<DocumentRecord>;
  estimates: List<EstimateOverviewRecord>;
  inspections: List<EstimateOverviewRecord>;
  assignments: List<JobAssignmentRecord>;
  additional_assignment: Nullable<JobAssignmentRecord>;
  additional_info: string;
  metadata: Map<any, any>;
  appointments: List<Map<"start_time", Date>>;
  jobNameChanged: boolean;

  errors: List<string>;

  created_at: Date;
  updated_at: Date;
}

export const defaultJobProps = {
  id: 0,
  uid: "",
  uuid: "",
  org_id: 0,
  org_uid: "",
  name: "",
  customer_name: "",
  state: "New",
  stateAction: "",
  config: Map(),
  raw_state: "new",
  lead_source: "Manual",
  email: "",
  measurement_id: 0,
  viewed: false,
  loading: false,
  use_job_address: "yes" as YesNoProp,
  events: List<string>(),
  job_type_ids: List<number>(),
  folder_id: null,
  folders: Map<RootFolderNames, number>(),
  address: addressFromJSON({}),
  customer_address: addressFromJSON({}),
  cover_image_uuid: null,
  estimate_count: 0,
  archived: false,
  document_count: 0,
  image_count: 0,
  images: List<ImageRecord>(),
  documents: List<DocumentRecord>(),
  phone_numbers: List<PhoneNumberRecord>(),
  estimates: List<EstimateOverviewRecord>(),
  inspections: List<EstimateOverviewRecord>(),
  assignments: List<JobAssignmentRecord>(),
  additional_assignment: null,
  additional_info: "",
  metadata: Map(),
  appointments: List<Map<"start_time", Date>>(),
  errors: List<string>(),
  jobNameChanged: false,

  created_at: new Date(),
  updated_at: new Date(),
};

export class JobRecord extends Record<IJobRecord>(defaultJobProps) implements IJobRecord {}
